import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalActionsComponent, SelectIdentitiesComponent } from '@app/components';

import { useTypedTranslation } from '@app/hooks';
import { getLoaderBtn } from '@app/store/actions';
import { IActiveFilter, IClientPermissionDialogData, Order } from '@app/models';
import { agentsService } from '@app/services';
import { AxiosResponse } from 'axios';
import { dialogComponentsListData, onPressEnterModal } from '@app/helpers';

interface IProps {
    clientId: string;
    onPermissionsAdded: Function;
    toggleModal: Function;
}

export const AddPermissionsToOauth2: React.SFC<IProps> = ({ clientId, onPermissionsAdded, toggleModal }) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const availableOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);
    const [orgs] = useState(availableOrgs.map(({ id, name }: any) => ({ Id: id, Name: name })));
    const [filteredPermissions, setFilteredPermissions] = useState<IClientPermissionDialogData[]>([]);
    const [localPermissions, setLocalPermissions] = useState<IClientPermissionDialogData[]>([]);
    const [selectAllLocal, setSelectAllLocal] = useState(false);
    const [missingPermissions, setMissingPermissions] = useState<any[]>([]);
    const [missingPermissionsCount, setMissingPermissionsCount] = useState<number>();
    const [loadMore, setLoadMore] = useState<number>(dialogComponentsListData);
    const [hasMoreItems, setHasMoreItems] = useState(false);
    const [isChecked, setIsChecked] = useState(true);
    const [dataLoading, isDataLoading] = useState<boolean>(false);

    useEffect(() => {
        handleGetMissingClientPermissions('Name', Order.asc, dialogComponentsListData, 0, true);
    }, []);

    useEffect(() => {
        if (missingPermissions) {
            const selectedPermissions: string[] = filteredPermissions.filter((el: any) => {
                return el.checked === true;
            }).map((el: any) => el.id as string);

            let newRows = missingPermissions.map((permission: any) => {
                const data: IClientPermissionDialogData = {
                    checked: false,
                    odataType: permission['@odata.type'],
                    name: permission.Name,
                    id: permission.Id,
                    organizationId: permission.OrganizationId,
                    permissions: permission.Permissions,
                    tenantId: permission.TenantId
                };

                selectedPermissions.forEach((permission: string) => {
                    if (data.id === permission) {
                        data.checked = true;
                    }
                });

                return data;
            });

            if (missingPermissions.length === 0) {
                setFilteredPermissions([]);
                setSelectAllLocal(false);
            } else {
                setLocalPermissions(newRows);
                setFilteredPermissions(newRows);
            }
        }
    }, [missingPermissions]);

    const handleGetMissingClientPermissions = (
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ) => {
        isDataLoading(true);
        agentsService.getMissingClientPermissions(clientId, orderBy, asc, top, skip, isReset, filters)
            .then((response: AxiosResponse) => {
                setMissingPermissionsCount(response.data['@odata.count']);
                if (filters === undefined) {
                    setMissingPermissions(missingPermissions.concat(response.data.value));
                } else if (filters.length) {
                    setMissingPermissions(response.data.value);
                }
            })
            .then(() => isDataLoading(false))
            .catch(() => {
                isDataLoading(false);
            });
    };

    const handleSetHasMore = (value: any) => {
        setHasMoreItems(value);
    };

    const handleSetValueFilter = (value: any) => {
        const filters: IActiveFilter[] = [
            {
                name: 'contains',
                value: value,
                label: 'DisplayName',
                filterBy: 'Name'
            }
        ];

        handleGetMissingClientPermissions('Name', Order.asc, loadMore, 0, true, filters);
    };

    const handleSetFiltered = (value: any) => {
        setFilteredPermissions(value);
    };

    const handleCheck = (value: boolean) => {
        setIsChecked(value);
    };

    const handleAddPermissions = () => {
        const selectedPermissions: string[] = filteredPermissions.filter((el: any) => {
            return el.checked === true;
        }).map((el: any) => el.id as string);

        dispatch(getLoaderBtn(true));
        agentsService.addClientPermissions(clientId, selectedPermissions, handleAddPermissionsSuccess, handleAddPermissionsError);
    };

    const handleAddPermissionsSuccess = () => {
        dispatch(getLoaderBtn(false));
        onPermissionsAdded();
    };

    const handleAddPermissionsError = (error: any) => { };

    const handleFetchMoreNonMemberedUsersInRole = () => {
        if (hasMoreItems) {
            handleGetMissingClientPermissions('Name', Order.asc, dialogComponentsListData, loadMore, true);
            setLoadMore(loadMore + dialogComponentsListData);
        }
    };

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleAddPermissions, isChecked)}>
            <SelectIdentitiesComponent
                filteredItems={filteredPermissions}
                localItems={localPermissions}
                nonMemberedItems={missingPermissions}
                orgs={orgs}
                selectAll={selectAllLocal}
                setFilteredItems={handleSetFiltered}
                setHandleCheck={handleCheck}
                noMoreResults={translation('NO_USERS_TO_ADD_TO_ROLE')}
                fetchMore={handleFetchMoreNonMemberedUsersInRole}
                totalCount={missingPermissionsCount}
                setHasMoreItems={handleSetHasMore}
                hasMoreItems={hasMoreItems}
                setValueFilter={handleSetValueFilter}
                additionalDisplayText={'organizations'}
                dataLoading={dataLoading}
            />
        </div>
            <ModalActionsComponent
                primaryBtn={translation('ADD_PERMISSIONS')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleAddPermissions}
                onClickSecondary={toggleModal}
                disabled={isChecked}
                id='addPermOAuth'
                withBorder={true}
            />
        </>
    );
};
